import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import * as moment from 'moment';
import { InstitutionService } from 'src/app/services/institution.service';
import { UserService } from 'src/app/services/user.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { PromptService } from 'src/app/services/prompt.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ForumService } from 'src/app/services/forum.service';
import { ToastService } from 'src/app/services/toast.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { Subscription } from 'rxjs';
import Comment from 'src/app/interfaces/Comment'; 
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'oc-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
})
export class CardComponent implements OnInit, OnDestroy {
  @Input() type: string;
  @Input() variation = '';
  @Input() data: any;
  @Input() clickable = false;
  @Input() currentUser: any;
  @Output() cardEvent: EventEmitter<any> = new EventEmitter();
  moment = moment;
  owner;
  commentOwners = [];
  replyOwners = [];
  cardOptions = false;
  editingContent = false;
  quillModules = {
    toolbar: [['link', 'bold', 'italic', 'underline', 'strike', 'blockquote', { list: 'ordered' }, { list: 'bullet' }]],
  };
  enterBindings = [];
  resourceImg;
  members = [];
  taggedMembers = [];
  subscriptions: Subscription = new Subscription();
  params = null;

  constructor(
    private institutionService: InstitutionService,
    private userService: UserService,
    private notificationService: NotificationsService,
    private promptService: PromptService,
    private loadingService: LoadingService,
    private forumService: ForumService,
    private toastService: ToastService,
    private analyticsService: AnalyticsService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    if (this.type === 'topic' || this.type === 'discussion' || this.type === 'resource') {
      this.userService.getUser(this.data.owner).then((o) => {
        this.owner = o;
      });
    }
    if (this.type === 'topic') {
      if (this.data.comments) {
        this.data.comments.forEach((c, i) => {
          if (!this.commentOwners.some((o) => c.uid === o.uid)) {
            this.commentOwners.push(c);
          }
        });
      }
    }
    if (this.type === 'discussion') {
      if (this.data.replies) {
        this.data.replies.forEach((r) => {
          this.userService.getUser(r.owner).then((u) => {
            this.replyOwners.push(u);
          });
        });
      }
      if (this.data.resourceData) {
        this.setResourceImg(this.data.resourceData);
      }
      this.subscriptions.add(this.userService.getMembers().subscribe((m) => (this.members = m)));
    }
    if (this.type === 'institution') {
      this.getInstitutionMembers();
    }

    // init url params
    this.subscriptions.add(
      this.route.queryParams.subscribe(params => {
        this.params = params;
      })
    );
  }

  // Discussion Cards
  like() {
    if (this.data.reactions && this.data.reactions.length > 0) {
      const ind = this.data.reactions.indexOf(this.currentUser.uid);
      if (ind !== -1) {
        this.data.reactions.splice(ind, 1);
      } else {
        this.data.reactions.push(this.currentUser.uid);
        this.notifyOfLike();
      }
    } else {
      this.data.reactions = [this.currentUser.uid];
      this.notifyOfLike();
    }

    this.cardEvent.emit({ type: 'update_' + (this.variation === 'comment' || this.variation === 'reply' ? 'comment' : this.type), id: this.data.id, update: { reactions: this.data.reactions } });
  }

  notifyOfLike() {
    if (this.type === 'resource') {
      this.trackEvent('resource_entry_like');
    } else {
      this.trackEvent('forum_discussion_like');
    }
    if (this.owner) {
      let url, notification, location;
      if (this.type === 'resource') {
        url = `/resources/${this.data.id}`;
        notification = `liked your <a href="${url}">resource</a>`;
        location = 'resource';
      } else if (this.type === 'discussion' && this.variation === 'comment') {
        url = `/forum/${this.data.discussion}?comment=${this.data.id}`;
        notification = `liked your <a href="${url}">comment</a>`;
        location = 'comment';
      } else if (this.type === 'discussion' && this.variation === 'reply') {
        url = `/forum/${this.data.discussion}?comment=${this.data.replyTo}&reply=${this.data.id}`;
        notification = `liked your <a href="${url}">reply</a>`;
        location = 'reply';
      } else if (this.type === 'discussion') {
        url = `/forum/${this.data.id}`;
        notification = `liked your <a href="${url}">discussion</a>`;
        location = 'discussion';
      }
      this.notificationService.saveNotification(
        this.owner.uid,
        this.currentUser.uid,
        notification,
        this.currentUser.displayName,
        this.currentUser.profilePicture
      );
      this.notificationService.sendNotifications({
        type: 'likes',
        author: this.owner.uid,
        comment: this.currentUser.displayName,
        url: url,
        location: location
      });
    }
  }

  reply(event) {
    this.cardEvent.emit({
      type: 'reply_comment',
      id: this.data.id,
      reply_to: this.variation === 'comment' ? 'comment' : 'main',
      cardEl: event.target.closest('.comment-wrap'),
    });
  }

  getInstitutionMembers() {
    this.institutionService.getInstitutionMembers(this.data.id).subscribe((m) => {
      if (m) {
        this.data.members = m.filter((m) => m.institutionRoles[this.data.id].status === 'active');
      }
    });
  }

  updateComment() {
    this.cardEvent.emit({ type: 'update_comment', id: this.data.id, update: { content: this.data.content } });
    this.editingContent = false;
  }

  deleteComment() {
    const typeLabel = this.type.substring(0, 1).toUpperCase() + this.type.substring(1);

    this.promptService.setPrompt({
      hidden: false,
      heading: 'Are you sure you want to delete this ' + this.type + '?',
      body: 'You cannot undo this action.',
      confirmText: 'Delete ' + this.type,
      rejectText: 'Nevermind',
    });
    this.promptService.userResponse.subscribe((res) => {
      if (res === 'confirm') {
        this.loadingService.startLoading();
        let tempComment = {...this.data};
        this.deleteExtraCommentData(this.data.id, tempComment)
        this.forumService.deleteComment(this.data.id).then((deleteRes) => {
          this.promptService.clearPrompt();
          this.loadingService.stopLoading();
          this.toastService.setToast(`<p class="mbn txt-c">Comment deleted</p>`);
        });
      } else if (res === 'reject') {
        this.promptService.clearPrompt();
      }
    });
  }

  deleteExtraCommentData(id, comment) {
    this.forumService.getDiscussion(comment.discussion).then((discussion: any) => {
      discussion.comments.forEach((c, i) => {
        if (id === c.id) {
          discussion.comments.splice(i, 1);
        }
      })
      this.forumService.updateDiscussion(discussion.id, { comments: discussion.comments }).then(() => {
        this.userService.user.subscribe((u) => {
          if (u) {
            if (u.comments.indexOf(id) > -1) u.comments.splice(u.comments.indexOf(id), 1);
            this.userService.saveUser(u.uid, u).then(() => {
              this.forumService.getAllComments().subscribe((comments) => {
                if (comments) {
                  comments.forEach(c => {
                    if (c.replyTo === id) {
                      let tempComment = {...c}
                      this.forumService.deleteComment(c.id).then(() => {
                        this.deleteExtraCommentData(c.id, tempComment);
                      })
                    }
                  })
                }
              })
            });
          }
        })
      });
    })
  }

  flagComment(d) {
    this.notificationService.sendNotifications({
      type: 'flag',
      content: 'comment',
      message: `A comment by ${d.ownerMeta.name} has been flagged.`,
      url: window.location.href,
    });
    this.toastService.setToast(`<p class="mbn txt-c">Comment flagged.</p>`);
  }

  trackEvent(name: string): void {
    this.analyticsService.event(name);
  }

  timeSince(date) {
    // @ts-ignore
    var seconds = Math.floor((new Date() - date) / 1000);
    var interval = Math.floor(seconds / 31536000);
    if (interval > 1) {
      return interval + ' years';
    }
    interval = Math.floor(seconds / 2592000);
    if (interval > 1) {
      return interval + ' months';
    }
    interval = Math.floor(seconds / 86400);
    if (interval > 1) {
      return interval + ' days';
    }
    interval = Math.floor(seconds / 3600);
    if (interval > 1) {
      return interval + ' hours';
    }
    interval = Math.floor(seconds / 60);
    if (interval > 1) {
      return interval + ' minutes';
    }
    return Math.floor(seconds) + ' seconds';
  }

  formatDateStr(start, end) {
    if (start === end || end === null) {
      // same date
      return moment(start).format('D MMMM YYYY');
    } else if (moment(start).month() === moment(end).month()) {
      // same month different day
      return moment(start).date() + ' - ' + moment(end).format('D MMMM YYYY');
    } else if (moment(start).year() === moment(end).year()) {
      // same year different month
      return moment(start).format('D MMM') + ' - ' + moment(end).format('D MMM YYYY');
    } else {
      // different year
      return moment(start).format('D MMM YYYY') + ' - ' + moment(end).format('D MMM YYYY');
    }
  }

  setResourceImg(resource) {
    if (resource.resourceMeta) {
      if (resource.resourceMeta.image.charAt(0) === '/') {
        const url = new URL(resource.resourceUrl).origin;
        this.resourceImg = url + resource.resourceMeta.image;
      } else {
        this.resourceImg = resource.resourceMeta.image;
      }
    }
  }

  tagMember(m) {
    this.taggedMembers.push({ email: m.email, displayName: m.displayName, uid: m.uid });
  }
  editorOpened(editor) {
    this.enterBindings = editor.quillEditor.keyboard.bindings[13].map((b) => Object.assign({}, b))
    delete editor.quillEditor.keyboard.bindings[13]
  }
  editorClosed(editor) {
    if (!editor.quillEditor.keyboard.bindings[13]) {
      editor.quillEditor.keyboard.bindings[13] = this.enterBindings.map((b) => Object.assign({}, b))
    }
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }  
}
